import React from 'react';
import { Compass, Rocket, Target, Users, Zap } from 'lucide-react';
import { quotaBullets } from '../../lib/plans';

/** Puce d'un forfait. La forme objet met la ligne en avant (ambre) au lieu du gris
 *  courant : réservé aux arguments qui doivent accrocher l'œil sur la carte. */
export type Bullet = string | { text: string; highlight: true };

export const bulletText = (b: Bullet): string => (typeof b === 'string' ? b : b.text);
export const bulletHighlighted = (b: Bullet): boolean => typeof b !== 'string' && b.highlight;

export interface Tier {
  id: string;
  name: string;
  icon: React.ReactNode;
  accent: string;
  accentBg: string;
  tagline: string;
  promise: string;
  monthly: number | null;
  /** Conservé pour le comparatif historique ; plus affiché nulle part. */
  annual: number | null;
  priceNote: string;
  inheritsFrom: string | null;
  cta: string;
  ctaStyle: 'primary' | 'ghost';
  highlight?: boolean;
  /** Étiquette affichée au-dessus de la carte. */
  badge?: string;
  /** `live` : souscriptible aujourd’hui. `coming_soon` : affiché pour l’ancrage de prix. */
  status: 'live' | 'coming_soon';
  /** false = hors grille, rendu en ligne de contact sous les cartes. */
  inGrid: boolean;
  /** Pré-remplit le champ message de la modale bêta, pour capter l’intention. */
  interestMessage?: string;
  /** Contrepartie demandée, affichée sous le CTA. */
  counterpart?: string;
  bullets: Bullet[];
}

export const TIERS: Tier[] = [
  // ── Offres ouvertes aujourd'hui ────────────────────────────────────────────
  {
    id: 'beta',
    name: 'Bêta',
    icon: <Rocket size={20} />,
    accent: 'text-amber-400',
    accentBg: 'bg-amber-500/10',
    tagline: 'Accès anticipé',
    promise: 'Le produit complet, pendant qu’on le construit avec vous.',
    monthly: 0,
    annual: 0,
    priceNote: '',
    inheritsFrom: null,
    cta: 'Rejoindre la bêta',
    ctaStyle: 'primary',
    badge: 'Places limitées',
    status: 'live',
    inGrid: true,
    bullets: [
      'Toutes les fonctionnalités actuelles',
      ...quotaBullets('beta'),
      'Accompagnement direct par l’équipe',
    ],
  },
  {
    id: 'pilote',
    name: 'Pilote',
    icon: <Compass size={20} />,
    accent: 'text-indigo-400',
    accentBg: 'bg-indigo-500/10',
    tagline: 'La bêta sans les freins',
    promise: 'Le volume d’une vraie activité, et votre mot à dire sur la suite.',
    monthly: 59,
    annual: null,
    priceNote: 'tarif bêta · sans engagement',
    inheritsFrom: null,
    cta: 'Demander un pilote',
    ctaStyle: 'primary',
    // La carte encadrée d'ambre est celle qu'on veut vendre : Pilote, pas la bêta gratuite.
    highlight: true,
    badge: 'Exclusif pendant la bêta',
    status: 'live',
    inGrid: true,
    interestMessage: 'Pilote — ',
    bullets: [
      ...quotaBullets('pilote'),
      { text: 'Nouvelles fonctionnalités en avant-première, avant la sortie', highlight: true },
      { text: 'Participer à la roadmap produit', highlight: true },
      'Onboarding accompagné',
    ],
  },

  // ── À venir : annoncés pour l'ancrage, sans détail de fonctionnalités ───────
  {
    id: 'sourcer',
    name: 'Sourcer',
    icon: <Target size={20} />,
    accent: 'text-slate-400',
    accentBg: 'bg-slate-800',
    tagline: 'Le travail réel',
    promise: 'Tout l’arsenal du recruteur indépendant.',
    monthly: 99,
    annual: 82,
    priceNote: '1 siège',
    inheritsFrom: null,
    cta: "Ça m'intéresse",
    ctaStyle: 'ghost',
    badge: 'À venir',
    status: 'coming_soon',
    inGrid: true,
    bullets: [],
  },
  {
    id: 'squad',
    name: 'Squad',
    icon: <Users size={20} />,
    accent: 'text-slate-400',
    accentBg: 'bg-slate-800',
    tagline: 'L’équipe',
    promise: 'Staffez à plusieurs, sans perdre le fil.',
    monthly: 299,
    annual: 249,
    priceNote: '3 sièges inclus · +79 €/siège',
    inheritsFrom: null,
    cta: "Ça m'intéresse",
    ctaStyle: 'ghost',
    badge: 'À venir',
    status: 'coming_soon',
    inGrid: true,
    bullets: [],
  },
  {
    id: 'entreprise',
    name: 'Entreprise',
    icon: <Zap size={20} />,
    accent: 'text-slate-400',
    accentBg: 'bg-slate-800',
    tagline: 'Souveraineté & échelle',
    promise: 'Pour les ESN qui veulent tout maîtriser.',
    monthly: null,
    annual: null,
    priceNote: 'à partir de 5 sièges',
    inheritsFrom: null,
    cta: 'Parlons-en',
    ctaStyle: 'ghost',
    badge: 'À venir',
    status: 'coming_soon',
    inGrid: true,
    bullets: [],
  },
];
